/**
 * "Known shapes only" checkout error handling (issue #609; allow-list
 * rework issue #610 F-2/F-5).
 *
 * `POST v1/subscriptions/payments` fails in a small, pinned set of shapes
 * (see the "Contracts pinned" table on issue #609) — every 402/403/409 body
 * is wrapped by `ProcessRequestResponseData` as `{result: {...}}`, so the
 * Angular `HttpErrorResponse` exposes it at `error.error.result.*`. This
 * classifier turns that raw error into a discriminated union the component
 * switches on; anything that does not match a pinned shape becomes
 * `unknown` (or, for a 403, the generic `refused` copy) so the caller falls
 * back to the same fixed copy master always showed (AC-5) rather than
 * guessing at a message it cannot vouch for.
 *
 * issue #610 F-2 (regression): the original guard was a DENY list —
 * `isSafeUserMessage()` rejected only messages matching a handful of known
 * Stripe id prefixes (`sub|cus|pm|in|ch|pi|src|cn`) and showed everything
 * else verbatim. A real backend 403 "No such price: 'price_…'" (a `price_`
 * id, not in that list) reached the customer unchanged, and a stubbed
 * `pm_card_visa` did too (the tail after `pm_` is only 4 characters, under
 * the old `{6,}` floor). A deny list can only ever be as complete as the
 * set of bad patterns someone thought to enumerate — a new Stripe resource
 * prefix, or the backend forwarding a raw message for a shape nobody
 * pinned, both slip through by default.
 *
 * The guard is now an ALLOW list at the shape level, not the message level:
 * `classifyCheckoutError` only ever shows the backend's own text for a
 * response it recognises as one of the pinned shapes below (by `code` where
 * flymsg-be #992 sends one, else by the message's own wording, matched the
 * same defensive way `isNodeCouponRejection` and PR #606's
 * `trialRefusalMessage()` do). Every other shape — an unsanitised Stripe API
 * error, a 403 nobody has pinned yet — gets the fixed copy regardless of
 * what its text looks like, never a "does this look safe" guess. Once a
 * shape IS recognised, `isSafeUserMessage()` still runs as a second,
 * independent layer (defense in depth, now with a wider id pattern) in case
 * the pinned shape's own text ever embeds a Stripe id.
 */

/** Shown only when nothing pinned below matched (AC-5, issue #610 F-2). */
export const CHECKOUT_GENERIC_ERROR_MESSAGE =
  "Oops Sorry. Please try again. If this happens again, please contact heysupport@vengreso.com";

/** Non-`card_error` Stripe.js failures on `confirmCardPayment` (AC-2). */
export const CHECKOUT_3DS_REFUSED_MESSAGE =
  "We couldn't authorise this payment with your bank. Please try again or use a different card.";

/** 3-D Secure success poll timed out (AC-2). */
export const CHECKOUT_FINALISING_MESSAGE =
  "Your payment was authorised and your subscription is being finalised — check Settings → Subscription in a moment.";

/** Fallback wording for the still-confirming 403 until flymsg-be ships `code` (AC-3). */
export const CHECKOUT_CONFIRMATION_PENDING_FALLBACK =
  "We're still confirming your payment with Stripe. This can take a few seconds. Please wait a moment, then check your subscription status before trying again.";

export type CheckoutFailure =
  | { kind: "declined"; message: string }
  | { kind: "requires_action"; clientSecret: string; message: string }
  | { kind: "confirmation_pending"; message: string }
  | { kind: "refused"; message: string; couponInvalid: boolean }
  | { kind: "validation"; message: string }
  | { kind: "server_error" }
  | { kind: "deactivated"; adminEmail?: string }
  | { kind: "unknown" };

/**
 * A Stripe resource id, e.g. `sub_…`, `price_1QaQa610Missing99`,
 * `pm_card_visa`, `seti_…`. Deliberately wide (issue #610 F-2): rather than
 * enumerate every current Stripe object prefix, this matches "a short
 * lowercase word, or two joined by an underscore, then an underscore and an
 * alphanumeric/underscore tail" — the actual shape of every Stripe id in
 * the finding's evidence, including `pm_card_visa` (a 4-letter tail the old
 * `{6,}`-character floor missed) and `price_1QaQa610Missing99`. Ordinary
 * English prose essentially never contains an underscore, so this stays
 * specific to ids without needing a prefix allow-list of its own.
 */
const STRIPE_ID_RE = /\b[a-z]{2,10}(?:_[a-z]{2,10})?_[A-Za-z0-9_]{3,}/;

/** True when `text` contains what looks like a Stripe resource id. */
function containsStripeId(text: string): boolean {
  return STRIPE_ID_RE.test(text);
}

/**
 * The same heuristic as flymsg-be's `userSafeStripeMessage()`, widened
 * (issue #610 F-2): a message containing a Stripe object id, a URL,
 * `@stripe.com` or `errno` is never shown verbatim. The backend already
 * guards this on its side; this is defense in depth for whatever reaches
 * the FE anyway, applied only AFTER `classifyCheckoutError` has already
 * decided — from the response's shape, not this function — that the
 * message is eligible to be shown at all (see the module doc comment).
 */
export function isSafeUserMessage(text: unknown): text is string {
  if (typeof text !== "string" || text.trim().length === 0) {
    return false;
  }
  if (containsStripeId(text)) return false;
  if (/https?:\/\//i.test(text)) return false;
  if (/@stripe\.com/i.test(text)) return false;
  if (/errno/i.test(text)) return false;
  return true;
}

/** `message` if it is a pinned-safe user message, else `fallback`. */
function safe(message: unknown, fallback: string): string {
  return isSafeUserMessage(message) ? message : fallback;
}

/**
 * `message` contains "discount code" and ("not valid" or "already used") —
 * the same substring rule `isNodeCouponRejection` uses, chosen so a wording
 * tweak upstream doesn't silently stop matching, while staying specific
 * enough to never match an unrelated refusal.
 */
function isInvalidCouponShape(message: string): boolean {
  const normalized = message.toLowerCase();
  return normalized.includes("discount code") && (normalized.includes("not valid") || normalized.includes("already used"));
}

/** "You are already Subscribed to <plan title> plan" (`PaymentController.php:616,695`). */
function isAlreadySubscribedShape(message: string): boolean {
  return /already subscribed/i.test(message);
}

/** "A checkout for your account is already in progress…" (`PaymentController.php:522-524`). */
function isConcurrentCheckoutShape(message: string): boolean {
  const normalized = message.toLowerCase();
  return normalized.includes("checkout") && normalized.includes("already in progress");
}

/**
 * Three backend-authored 403 messages issue #609's contract table (AC-4,
 * "show verbatim") named but the round-1/round-2 allow-list left unpinned
 * (QA #610 round-2's "Spec note for the owner"). Sourced verbatim from
 * flymsg-be `origin/master`'s `PaymentController.php`:
 *  - `userSafeStripeMessage()`'s `ApiConnectionException` branch (line
 *    ~1064): "We could not reach Stripe to complete your checkout. Please
 *    try again in a moment."
 *  - `userSafeStripeMessage()`'s identifying-detail fallback (line ~1085):
 *    "Something went wrong processing your payment. Please try again."
 *  - the concurrent-swap guard (lines ~1375, ~1486): "Your plan change is
 *    already being processed. Please wait a moment and try again."
 * All three reach this endpoint as a plain 403 `{"message": "..."}"`
 * (`ApiErrorException`/`Exception` catch blocks, `PaymentController.php:1021,1025`),
 * wrapped into `{"result": {...}}` by the same no-`error`-key middleware
 * rule the 402 `PaymentIncompleteException` shape already relies on.
 * Exact string match, not a substring rule — unlike the shapes above (whose
 * wording deliberately tolerates drift), these three are pinned by the
 * literal text product wants shown, so a wording change upstream should
 * fail this match rather than silently keep matching something else.
 */
const PINNED_EXACT_403_MESSAGES = new Set<string>([
  "We could not reach Stripe to complete your checkout. Please try again in a moment.",
  "Something went wrong processing your payment. Please try again.",
  "Your plan change is already being processed. Please wait a moment and try again.",
]);

/** True when `message` is exactly one of `PINNED_EXACT_403_MESSAGES` (trimmed). */
function isPinnedExactMessage(message: string): boolean {
  return PINNED_EXACT_403_MESSAGES.has(message.trim());
}

/**
 * Recognises flymsg-be #983's still-confirming 403. flymsg-be #992 sends
 * `code: "payment_confirmation_pending"`; until/unless a caller has it, the
 * interim recogniser is the message substring pinned on issue #609 —
 * "still confirming" AND "payment", case-insensitive.
 */
function isConfirmationPendingShape(message: string, code: unknown): boolean {
  if (code === "payment_confirmation_pending") return true;
  const normalized = message.toLowerCase();
  return normalized.includes("still confirming") && normalized.includes("payment");
}

interface RawHttpError {
  status?: number;
  error?: unknown;
}

function resultOf(error: unknown): Record<string, unknown> | undefined {
  const body = (error as RawHttpError)?.error as { result?: unknown } | undefined;
  const result = body?.result;
  return result && typeof result === "object" ? (result as Record<string, unknown>) : undefined;
}

export function classifyCheckoutError(err: unknown): CheckoutFailure {
  const status = (err as RawHttpError)?.status;
  if (typeof status !== "number") {
    return { kind: "unknown" };
  }

  if (status >= 500) {
    return { kind: "server_error" };
  }

  // Top-level (not wrapped in `result`) — the deactivated-account middleware.
  const topLevelBody = (err as RawHttpError)?.error as { code?: unknown; admin_email?: unknown } | undefined;
  if (status === 403 && topLevelBody?.code === "DEACTIVATED") {
    return { kind: "deactivated", adminEmail: typeof topLevelBody.admin_email === "string" ? topLevelBody.admin_email : undefined };
  }

  if (status === 402) {
    // The only 402 shape this endpoint has (see the contracts table) — a
    // decline or a 3-D Secure challenge — is itself the pinned/known shape;
    // `safe()` still scrubs it for an embedded id as defense in depth.
    const result = resultOf(err);
    const message = result?.message;
    if (typeof message !== "string" || message.trim().length === 0) {
      return { kind: "unknown" };
    }
    const paymentStatus = result?.payment_status;
    const clientSecret = result?.client_secret;
    if (paymentStatus === "requires_action" && typeof clientSecret === "string" && clientSecret.length > 0) {
      return { kind: "requires_action", clientSecret, message: safe(message, CHECKOUT_GENERIC_ERROR_MESSAGE) };
    }
    // requires_payment_method / requires_confirmation, or requires_action
    // with no usable client_secret (AC-1) — a decline, not a challenge.
    return { kind: "declined", message: safe(message, CHECKOUT_GENERIC_ERROR_MESSAGE) };
  }

  if (status === 403) {
    const result = resultOf(err);
    const message = result?.message;
    const code = result?.code;
    const hasMessage = typeof message === "string" && message.trim().length > 0;
    const text = hasMessage ? (message as string) : "";

    // issue #610 F-5: flymsg-be #992 can send `code:
    // "payment_confirmation_pending"` on a body with no `message` at all —
    // recognise the shape by `code` FIRST, before requiring any text, so
    // the pending state still shows instead of falling through to
    // `unknown`/the generic Oops copy.
    if (code === "payment_confirmation_pending" || (hasMessage && isConfirmationPendingShape(text, code))) {
      return {
        kind: "confirmation_pending",
        message: hasMessage ? safe(text, CHECKOUT_CONFIRMATION_PENDING_FALLBACK) : CHECKOUT_CONFIRMATION_PENDING_FALLBACK,
      };
    }

    // issue #610 F-9 (round 2): the sibling of F-5's fix above — flymsg-be
    // #992's `code: "coupon_invalid"` must be recognised BEFORE the
    // `!hasMessage` bail-out below, exactly like `payment_confirmation_pending`
    // is. The `!hasMessage` guard used to run first, so a `coupon_invalid`
    // body with no `message` (or an empty one) fell through to `unknown`:
    // the customer still saw generic copy (acceptable — there is no text to
    // show verbatim either way), but `couponInvalid` was never set, so the
    // applied code was never marked invalid and a later Pay could resend
    // it. No current backend sends this shape (`coupon_invalid` is not
    // emitted anywhere today per QA's finding), but the allow-list must not
    // depend on a message being present to recognise a shape by its `code`.
    if (code === "coupon_invalid") {
      return {
        kind: "refused",
        message: hasMessage ? safe(text, CHECKOUT_GENERIC_ERROR_MESSAGE) : CHECKOUT_GENERIC_ERROR_MESSAGE,
        couponInvalid: true,
      };
    }

    if (!hasMessage) {
      return { kind: "unknown" };
    }

    // issue #610 F-2 — allow-list, not deny-list: only the shapes pinned on
    // issue #609 (already-subscribed, invalid/reused coupon — plus #992's
    // `coupon_invalid` code, handled above) ever show the backend's own
    // text here. Every other 403 — an unsanitised "Any other Stripe API
    // error" body (e.g. "No such price: 'price_…'"), or a shape nobody has
    // pinned yet — always gets the fixed copy, regardless of whether its
    // text happens to look "safe": that is exactly how a `price_` id
    // reached the customer despite the old deny list not matching it.
    if (isInvalidCouponShape(text)) {
      return { kind: "refused", message: safe(text, CHECKOUT_GENERIC_ERROR_MESSAGE), couponInvalid: true };
    }

    if (isAlreadySubscribedShape(text)) {
      return { kind: "refused", message: safe(text, CHECKOUT_GENERIC_ERROR_MESSAGE), couponInvalid: false };
    }

    // issue #610 allow-list (round 2, QA's "Spec note for the owner"): the
    // three pinned exact strings above — a Stripe-unreachable 403, the
    // sanitized-Stripe-error fallback, and the concurrent-plan-change guard
    // — are backend-authored and safe, and issue #609's contract table
    // already named them for verbatim display.
    if (isPinnedExactMessage(text)) {
      return { kind: "refused", message: safe(text, CHECKOUT_GENERIC_ERROR_MESSAGE), couponInvalid: false };
    }

    return { kind: "refused", message: CHECKOUT_GENERIC_ERROR_MESSAGE, couponInvalid: false };
  }

  if (status === 409) {
    const result = resultOf(err);
    const message = result?.message;
    if (typeof message !== "string" || message.trim().length === 0) {
      return { kind: "unknown" };
    }
    // Only one 409 shape is pinned (concurrent checkout) — same allow-list
    // discipline as 403: an unrecognised 409 body gets the fixed copy.
    if (isConcurrentCheckoutShape(message)) {
      return { kind: "refused", message: safe(message, CHECKOUT_GENERIC_ERROR_MESSAGE), couponInvalid: false };
    }
    return { kind: "refused", message: CHECKOUT_GENERIC_ERROR_MESSAGE, couponInvalid: false };
  }

  if (status === 422) {
    // Laravel validation — NOT wrapped in `result` (unlike every other
    // status here). Never the old "Your bank declined this transaction"
    // copy: 422 is request validation, never a decline (issue #609).
    const body = (err as RawHttpError)?.error as { error_list?: Record<string, string[]> } | undefined;
    const errorList = body?.error_list;
    if (errorList && typeof errorList === "object") {
      for (const key of Object.keys(errorList)) {
        const messages = errorList[key];
        if (Array.isArray(messages) && typeof messages[0] === "string" && messages[0].trim().length > 0) {
          return { kind: "validation", message: messages[0] };
        }
      }
    }
    return { kind: "unknown" };
  }

  return { kind: "unknown" };
}
